import { ApiError } from '@/shared/types/api.types';

/** Verification refused the file; `rejectionCode` says why. */
export class UploadRejectedError extends Error {
  readonly rejectionCode: string | null;

  constructor(rejectionCode: string | null) {
    super(rejectionMessage(rejectionCode));
    this.name = 'UploadRejectedError';
    this.rejectionCode = rejectionCode;
  }
}

/** Problem `code`s an upload can end with, in words a person can act on. */
const MESSAGES: Readonly<Record<string, string>> = {
  file_type_not_allowed: 'This kind of file cannot be uploaded as this document type.',
  file_too_large: 'The file is larger than this document type allows.',
  upload_expired: 'The upload took too long and expired. Upload the file again.',
  upload_file_mismatch: 'The file changed while it was being uploaded. Upload it again.',
  duplicate_revision_content: 'This file is identical to the current revision.',
  document_number_taken: 'Another document in this project already has this number.',
  storage_unavailable: 'File storage is unavailable. Try again in a few minutes.',
  storage_cors_misconfigured:
    'File storage does not accept uploads from this site yet. Ask your administrator to check its CORS settings.',
  forbidden: 'Your project role does not allow this upload.',
};

/** Verification `rejectionCode`s. */
const REJECTIONS: Readonly<Record<string, string>> = {
  file_signature_mismatch: "The file's contents do not match its type.",
  checksum_mismatch: 'The file was damaged on the way to storage. Upload it again.',
  size_mismatch: 'The file arrived incomplete. Upload it again.',
  macro_enabled_content: 'Files with macros are not accepted.',
  archive_limits_exceeded: 'The file contains too many or too large embedded parts.',
  malware_detected: 'The file was flagged as malware.',
};

export function rejectionMessage(code: string | null): string {
  return (code === null ? undefined : REJECTIONS[code]) ?? 'The file did not pass verification.';
}

export function uploadErrorMessage(error: unknown): string {
  if (error instanceof UploadRejectedError) return error.message;
  if (!(error instanceof ApiError)) return 'The upload failed.';
  const known = error.code === undefined ? undefined : MESSAGES[error.code];
  if (known !== undefined) return known;
  if (error.issues.length > 0) return error.issues.flatMap((issue) => issue.messages).join(' ');
  return error.message;
}
